// Simple notification service using existing API
import apiClient from "../api/apiClient";

class SimpleNotificationService {
  private intervalId: NodeJS.Timeout | null = null;
  private listeners: Set<(notifications: any[]) => void> = new Set();
  private isPolling: boolean = false;
  private lastCheck: Date | null = null;
  private seenNotificationIds: Set<string> = new Set();
  private pollingInterval: number = 10000; // 10 seconds

  constructor() {
    // Load seen notifications from localStorage
    this.loadSeenNotifications();
  }

  private loadSeenNotifications() {
    try {
      const stored = localStorage.getItem("seenNotificationIds");
      if (stored) {
        const ids = JSON.parse(stored);
        this.seenNotificationIds = new Set(ids);
        // console.log(
        //   `📋 Loaded ${this.seenNotificationIds.size} seen notification IDs`
        // );
      }
    } catch (error) {
      console.warn("Failed to load seen notifications:", error);
      this.seenNotificationIds = new Set();
    }
  }

  private saveSeenNotifications() {
    try {
      const ids = Array.from(this.seenNotificationIds);
      // Keep only the last 100 IDs to prevent localStorage from growing too large
      const recentIds = ids.slice(-100);
      localStorage.setItem("seenNotificationIds", JSON.stringify(recentIds));
      this.seenNotificationIds = new Set(recentIds);
    } catch (error) {
      console.warn("Failed to save seen notifications:", error);
    }
  }

  // Start checking for new notifications
  public startChecking() {
    if (this.isPolling) {
      console.log("📊 Already checking for notifications");
      return;
    }

    this.isPolling = true;
    this.lastCheck = new Date();
    // console.log("📊 Starting notification checking...");

    // Check immediately, then at intervals
    this.checkForNewNotifications();

    this.intervalId = setInterval(() => {
      this.checkForNewNotifications();
    }, this.pollingInterval);
  }

  private async checkForNewNotifications() {
    try {
      // console.log("📊 Checking for new notifications...");

      const response = await apiClient.get("/notification");
      const allNotifications = response.data || [];

      if (!Array.isArray(allNotifications)) {
        console.warn("Invalid notifications response:", allNotifications);
        return;
      }

      // Filter out notifications we've already seen
      const newNotifications = allNotifications.filter((notification: any) => {
        const isNew = !this.seenNotificationIds.has(notification.id);
        if (isNew) {
          // Add to seen notifications immediately
          this.seenNotificationIds.add(notification.id);
        }
        return isNew;
      });

      if (newNotifications.length > 0) {
        // console.log(
        //   `📊 Found ${newNotifications.length} new notifications:`,
        //   newNotifications
        // );

        // Save updated seen notifications
        this.saveSeenNotifications();

        // Notify all listeners about new notifications
        this.listeners.forEach((listener) => {
          try {
            listener(newNotifications);
          } catch (error) {
            // console.error("❌ Error in notification listener:", error);
          }
        });
      } else {
        // console.log("📊 No new notifications found");
      }

      this.lastCheck = new Date();
    } catch (error) {
      // console.error("❌ Error checking for notifications:", error);
    }
  }

  // Stop checking
  public stopChecking() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.isPolling = false;
    // console.log("📊 Stopped notification checking");
  }

  // Add listener for new notifications
  public addListener(callback: (notifications: any[]) => void) {
    this.listeners.add(callback);
    // console.log(
    //   `👂 Added notification listener (total: ${this.listeners.size})`
    // );

    return () => {
      this.listeners.delete(callback);
      console.log(
        `🗑️ Removed notification listener (remaining: ${this.listeners.size})`
      );
    };
  }

  // Get service status
  public getStatus() {
    return {
      isPolling: this.isPolling,
      lastCheck: this.lastCheck,
      seenCount: this.seenNotificationIds.size,
      pollingInterval: this.pollingInterval,
    };
  }

  // Manually check now
  public async checkNow() {
    // console.log("📊 Manual check requested");
    await this.checkForNewNotifications();
  }

  // Reset seen notifications (useful for testing)
  public resetSeenNotifications() {
    this.seenNotificationIds.clear();
    localStorage.removeItem("seenNotificationIds");
    // console.log("🔄 Reset seen notifications");
  }

  // Mark specific notifications as seen (to prevent re-processing)
  public markAsSeen(notificationIds: string[]) {
    notificationIds.forEach((id) => this.seenNotificationIds.add(id));
    this.saveSeenNotifications();
    // console.log(`✅ Marked ${notificationIds.length} notifications as seen`);
  }

  // Set polling interval
  public setPollingInterval(intervalMs: number) {
    this.pollingInterval = Math.max(5000, intervalMs); // Minimum 5 seconds
    // console.log(`⏱️ Polling interval set to ${this.pollingInterval}ms`);

    // Restart if currently polling
    if (this.isPolling) {
      this.stopChecking();
      this.startChecking();
    }
  }

  // Cleanup
  public cleanup() {
    // console.log("🧹 Cleaning up notification service...");
    this.stopChecking();
    this.listeners.clear();
  }
}

// Export singleton instance
export const simpleNotificationService = new SimpleNotificationService();
